import api from './api';
import {
  VolunteerShift,
  VolunteerShiftDetail,
  VolunteerShiftRegistration,
  VolunteerProfile,
  VolunteerStatusResponse,
  VolunteerApplicationData,
  VolunteerUser,
  VolunteerReports,
  CreateShiftData,
  VolunteerSkill,
} from '../types/volunteer';

// --- Volunteer User Endpoints ---

export const applyAsVolunteer = (data: VolunteerApplicationData) =>
  api.post('/volunteers/apply', data);

export const getVolunteerStatus = () =>
  api.get<VolunteerStatusResponse>('/volunteers/status');

export const getVolunteerProfile = () =>
  api.get<VolunteerProfile>('/volunteers/profile');

export const getAvailableShifts = (params?: Record<string, string>) =>
  api.get<VolunteerShift[]>('/volunteers/shifts', { params });

export const getShiftDetail = (shiftId: string) =>
  api.get<VolunteerShiftDetail>(`/volunteers/shifts/${shiftId}`);

export const registerForShift = (shiftId: string, roleId?: string) =>
  api.post(`/volunteers/shifts/${shiftId}/register`, { roleId });

export const cancelShiftRegistration = (shiftId: string) =>
  api.put(`/volunteers/shifts/${shiftId}/cancel`);

export const selfCheckIn = (shiftId: string) =>
  api.put<VolunteerShiftRegistration>(`/volunteers/shifts/${shiftId}/check-in`);

export const selfCheckOut = (shiftId: string) =>
  api.put<VolunteerShiftRegistration>(`/volunteers/shifts/${shiftId}/check-out`);

export const getMyShifts = (hidePast?: boolean) =>
  api.get<VolunteerShiftRegistration[]>('/volunteers/my-shifts', {
    params: hidePast ? { hidePast: 'true' } : undefined,
  });

export const addSkill = (name: string) =>
  api.post<VolunteerSkill>('/volunteers/skills', { name });

export const removeSkill = (skillId: string) =>
  api.delete(`/volunteers/skills/${skillId}`);

// --- Admin Volunteer Endpoints ---

export const getVolunteers = (params?: Record<string, string>) =>
  api.get<VolunteerUser[]>('/admin/volunteers', { params });

export const getVolunteerDetail = (userId: string) =>
  api.get(`/admin/volunteers/${userId}`);

export const approveVolunteer = (userId: string) =>
  api.put(`/admin/volunteers/${userId}/approve`);

export const rejectVolunteer = (userId: string) =>
  api.put(`/admin/volunteers/${userId}/reject`);

export const revokeVolunteer = (userId: string) =>
  api.put(`/admin/volunteers/${userId}/revoke`);

export const createVolunteerManually = (data: { email: string; firstName?: string; lastName?: string; phone?: string }) =>
  api.post('/admin/volunteers/create', data);

// --- Admin Shift Endpoints ---

export const createShift = (data: CreateShiftData) =>
  api.post<VolunteerShift>('/admin/volunteer-shifts', data);

export const updateShift = (shiftId: string, data: Partial<CreateShiftData> & { isActive?: boolean }) =>
  api.put<VolunteerShift>(`/admin/volunteer-shifts/${shiftId}`, data);

export const deleteShift = (shiftId: string) =>
  api.delete(`/admin/volunteer-shifts/${shiftId}`);

export const getAdminShifts = (params?: Record<string, string>) =>
  api.get<VolunteerShift[]>('/admin/volunteer-shifts', { params });

export const getShiftWithRegistrations = (shiftId: string) =>
  api.get(`/admin/volunteer-shifts/${shiftId}`);

export const adminRegisterForShift = (shiftId: string, userId: string, roleId?: string) =>
  api.post(`/admin/volunteer-shifts/${shiftId}/register`, { userId, roleId });

export const checkInVolunteer = (shiftId: string, registrationId: string) =>
  api.put(`/admin/volunteer-shifts/${shiftId}/check-in/${registrationId}`);

export const checkOutVolunteer = (shiftId: string, registrationId: string, notes?: string) =>
  api.put(`/admin/volunteer-shifts/${shiftId}/check-out/${registrationId}`, { notes });

export const verifySkill = (skillId: string) =>
  api.put(`/admin/volunteers/skills/${skillId}/verify`);

export const addShiftRole = (shiftId: string, name: string, capacity?: number) =>
  api.post(`/admin/volunteer-shifts/${shiftId}/roles`, { name, capacity });

export const removeShiftRole = (roleId: string) =>
  api.delete(`/admin/volunteer-shifts/roles/${roleId}`);

export const getVolunteerReports = () =>
  api.get<VolunteerReports>('/admin/volunteer-reports');
